import React from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  Building2, ShieldCheck, UserCog, Network, FileText, TrendingUp,
  Check, Landmark, CalendarClock, ArrowUpRight,
} from "lucide-react";

const BRASS = "#B08D57";
const BRASS2 = "#D7C09A";
const SAND = "#E7D7BF";
const TEXT = "#F4F1EA";
const TEXT2 = "#CFC7BA";
const MUTED = "#9B948A";
const HAIR = "rgba(231,215,191,0.22)";
const GREEN = "#6FB89A";

/* ---------- Faux app window ---------- */
export const MockWindow = ({ title, badge, children, className, "data-testid": testId, ...rest }) => (
  <div
    className={cn("mk-card relative overflow-hidden", className)}
    style={{ boxShadow: "0 24px 60px rgba(0,0,0,0.5)" }}
    data-testid={testId}
    {...rest}
  >
    <div className="flex h-9 items-center gap-2 border-b border-[color:var(--mk-border)] px-3.5">
      <span className="flex gap-1.5 opacity-60">
        <span className="h-2 w-2 rounded-full bg-[#C98E6E]" />
        <span className="h-2 w-2 rounded-full bg-[#C9A86A]" />
        <span className="h-2 w-2 rounded-full bg-[#6FB89A]" />
      </span>
      {title && <span className="ml-1 font-data text-[11px] text-[color:var(--mk-muted)]">{title}</span>}
      {badge && <span className="ml-auto">{badge}</span>}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

/* ---------- Ownership graph (SVG) ---------- */
export const OwnershipGraphMini = ({ className }) => {
  const nodes = [
    { l: "Family Trust", x: 160, y: 24, kind: "family" },
    { l: "HoldCo", x: 64, y: 84, kind: "entity" },
    { l: "NBFC", x: 256, y: 84, kind: "entity" },
    { l: "OpCo", x: 36, y: 148, kind: "leaf" },
    { l: "Pvt Ltd", x: 132, y: 148, kind: "leaf" },
    { l: "SPV", x: 256, y: 148, kind: "leaf" },
  ];
  const edges = [[0, 1, "100%"], [0, 2, "74%"], [1, 3, "60%"], [1, 4, "40%"], [2, 5, "51%"]];
  return (
    <svg viewBox="0 0 320 176" className={cn("h-full w-full", className)} role="img" aria-label="Beneficial ownership graph">
      {edges.map(([a, b, pct], i) => {
        const na = nodes[a], nb = nodes[b];
        const mx = (na.x + nb.x) / 2, my = (na.y + nb.y) / 2;
        return (
          <g key={i}>
            <line x1={na.x} y1={na.y} x2={nb.x} y2={nb.y} stroke={HAIR} strokeWidth="1.4" />
            <text x={mx} y={my - 3} fill={MUTED} fontSize="8" textAnchor="middle" fontFamily="Space Grotesk, sans-serif">{pct}</text>
          </g>
        );
      })}
      {nodes.map((n, i) => {
        const fill = n.kind === "family" ? BRASS : n.kind === "entity" ? "rgba(231,215,191,0.5)" : "rgba(231,215,191,0.28)";
        const w = Math.max(48, n.l.length * 7 + 18);
        return (
          <g key={i}>
            <rect x={n.x - w / 2} y={n.y - 11} width={w} height={22} rx={11}
              fill={n.kind === "family" ? "rgba(176,141,87,0.16)" : "rgba(16,27,43,0.9)"}
              stroke={n.kind === "family" ? BRASS : HAIR} strokeWidth="1" />
            <circle cx={n.x - w / 2 + 11} cy={n.y} r="3" fill={fill} />
            <text x={n.x + 4} y={n.y + 3} fill={n.kind === "family" ? BRASS2 : TEXT2} fontSize="9" textAnchor="middle" fontFamily="Manrope, sans-serif">{n.l}</text>
          </g>
        );
      })}
    </svg>
  );
};

/* ---------- Compliance calendar (mini) ---------- */
export const ComplianceCalendarMini = ({ className }) => {
  const due = { 6: "DPT-3", 14: "DIR-3", 22: "AOC-4" };
  return (
    <div className={cn("", className)}>
      <div className="mb-2 flex items-center justify-between">
        <span className="font-data text-[11px] text-[color:var(--mk-text-2)]">FY 2025-26</span>
        <Badge variant="outline" className="border-[color:var(--mk-border-strong)] text-[10px] text-[color:var(--mk-text-2)]">Statutory</Badge>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: 28 }).map((_, i) => {
          const d = i + 1;
          const isDue = due[d];
          return (
            <div key={i}
              className={cn(
                "flex aspect-square items-center justify-center rounded-[5px] text-[8px]",
                isDue
                  ? "bg-[rgba(176,141,87,0.18)] font-semibold text-[color:var(--mk-primary-2)] ring-1 ring-[color:var(--mk-primary)]/50"
                  : "bg-[rgba(231,215,191,0.04)] text-[color:var(--mk-muted)]"
              )}>
              {d}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Badge className="gap-1 border-[rgba(176,141,87,0.4)] bg-[rgba(176,141,87,0.16)] text-[10px] text-[color:var(--mk-text)]"><CalendarClock className="h-3 w-3" /> AOC-4 · 30 Oct</Badge>
        <Badge className="gap-1 border-[rgba(15,61,46,0.55)] bg-[rgba(15,61,46,0.4)] text-[10px] text-[color:var(--mk-text)]"><Check className="h-3 w-3" /> MGT-7 draft</Badge>
      </div>
    </div>
  );
};

/* ---------- DRAFT PDF stack ---------- */
export const PdfDraftStack = ({ className }) => {
  const watermark = {
    backgroundImage:
      "repeating-linear-gradient(135deg, rgba(176,141,87,0.10) 0, rgba(176,141,87,0.10) 10px, transparent 10px, transparent 22px)",
  };
  const forms = [
    { code: "AOC-4", t: "Financial Statements" },
    { code: "MGT-7", t: "Annual Return" },
  ];
  return (
    <div className={cn("relative", className)}>
      {forms.map((f, i) => (
        <div key={f.code}
          className="relative mb-2 overflow-hidden rounded-lg border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] p-3"
          style={{ marginLeft: i * 10 }}>
          <div className="pointer-events-none absolute inset-0" style={watermark} />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-[color:var(--mk-primary-2)]" />
              <div>
                <div className="font-data text-[11px] font-semibold text-[color:var(--mk-text)]">Form {f.code}</div>
                <div className="text-[9px] text-[color:var(--mk-muted)]">{f.t}</div>
              </div>
            </div>
            <Badge className="border-[rgba(176,141,87,0.4)] bg-[rgba(176,141,87,0.16)] text-[9px] uppercase tracking-wider text-[color:var(--mk-primary-2)]">Draft</Badge>
          </div>
          <div className="relative mt-2 space-y-1">
            <div className="h-1.5 w-full rounded bg-[rgba(231,215,191,0.10)]" />
            <div className="h-1.5 w-4/5 rounded bg-[rgba(231,215,191,0.08)]" />
          </div>
        </div>
      ))}
    </div>
  );
};

/* ---------- ABAC permission matrix (mini) ---------- */
export const AbacMatrixMini = ({ className }) => {
  const rows = [
    ["Corporate", true, true],
    ["Compliance", true, true],
    ["Documents", true, false],
    ["Portfolio", false, false],
  ];
  const Dot = ({ on }) => (
    on
      ? <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-[rgba(15,61,46,0.5)] text-[color:#6FB89A]"><Check className="h-2.5 w-2.5" /></span>
      : <span className="inline-block h-1 w-3 rounded bg-[rgba(231,215,191,0.18)]" />
  );
  return (
    <div className={cn("", className)}>
      <div className="mb-2 flex items-center justify-between">
        <span className="font-data text-[11px] text-[color:var(--mk-text-2)]">CA · Mehta & Co.</span>
        <Badge className="gap-1 border-[rgba(176,141,87,0.4)] bg-[rgba(176,141,87,0.16)] text-[9px] text-[color:var(--mk-primary-2)]"><CalendarClock className="h-3 w-3" /> expires 31 Mar</Badge>
      </div>
      <div className="overflow-hidden rounded-lg border border-[color:var(--mk-border)]">
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 bg-[rgba(16,27,43,0.7)] px-3 py-1.5 text-[9px] uppercase tracking-wider text-[color:var(--mk-muted)]">
          <span>Module</span><span>View</span><span>Edit</span>
        </div>
        {rows.map(([m, v, e]) => (
          <div key={m} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 border-t border-[color:var(--mk-border)] px-3 py-1.5 text-[10px] text-[color:var(--mk-text-2)]">
            <span>{m}</span><Dot on={v} /><Dot on={e} />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Performance chart (mini SVG) ---------- */
export const PerfChartMini = ({ className }) => {
  const bars = [28, 40, 34, 52, 46, 64, 58, 76];
  const max = 80;
  return (
    <div className={cn("", className)}>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {[["XIRR", "18.4%"], ["TVPI", "1.84x"], ["DPI", "0.62x"]].map(([k, v]) => (
          <span key={k} className="inline-flex items-baseline gap-1 rounded-md border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] px-2 py-1">
            <span className="text-[9px] uppercase tracking-wider text-[color:var(--mk-muted)]">{k}</span>
            <span className="font-data text-[11px] font-semibold text-[color:var(--mk-text)]">{v}</span>
          </span>
        ))}
      </div>
      <svg viewBox="0 0 240 90" className="h-full w-full" role="img" aria-label="Performance chart">
        {bars.map((b, i) => {
          const x = 8 + i * 29;
          const h = (b / max) * 70;
          return <rect key={i} x={x} y={80 - h} width="16" height={h} rx="3" fill="rgba(176,141,87,0.32)" />;
        })}
        <polyline
          fill="none" stroke={BRASS2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          points={bars.map((b, i) => `${16 + i * 29},${80 - (b / max) * 70}`).join(" ")}
        />
        {bars.map((b, i) => <circle key={i} cx={16 + i * 29} cy={80 - (b / max) * 70} r="2.4" fill={BRASS2} />)}
      </svg>
    </div>
  );
};

/* ---------- Hero console (composed) ---------- */
export const HeroConsole = () => {
  const navItems = [
    { icon: Building2, l: "Entities" },
    { icon: Landmark, l: "Corporate" },
    { icon: ShieldCheck, l: "Compliance" },
    { icon: UserCog, l: "Advisors" },
  ];
  return (
    <MockWindow
      title="Family Souq · Console"
      badge={<Badge className="border-[rgba(15,61,46,0.55)] bg-[rgba(15,61,46,0.4)] text-[10px] text-[color:var(--mk-text)]">Key-file secured</Badge>}
    >
      <div className="grid grid-cols-[112px_1fr] gap-3">
        {/* sidebar */}
        <aside className="space-y-1.5">
          {navItems.map((n, i) => (
            <div key={n.l}
              className={cn(
                "flex items-center gap-2 rounded-lg px-2.5 py-2 text-[11px]",
                i === 1
                  ? "bg-[rgba(176,141,87,0.14)] text-[color:var(--mk-text)]"
                  : "text-[color:var(--mk-text-2)]"
              )}>
              <n.icon className={cn("h-3.5 w-3.5", i === 1 ? "text-[color:var(--mk-primary-2)]" : "text-[color:var(--mk-muted)]")} />
              {n.l}
            </div>
          ))}
          <div className="mt-2 rounded-lg border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] p-2.5">
            <div className="text-[9px] uppercase tracking-wider text-[color:var(--mk-muted)]">Net worth</div>
            <div className="font-data text-sm font-semibold text-[color:var(--mk-text)]">₹1,284 Cr</div>
            <div className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] text-[#6FB89A]"><TrendingUp className="h-3 w-3" /> +6.2%</div>
          </div>
        </aside>
        {/* main */}
        <div className="space-y-3">
          <div className="rounded-xl border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] p-3">
            <div className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[color:var(--mk-muted)]">
              <Network className="h-3.5 w-3.5 text-[color:var(--mk-primary-2)]" /> Beneficial look-through
            </div>
            <div className="h-[150px]"><OwnershipGraphMini /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] p-3">
              <ComplianceCalendarMini />
            </div>
            <div className="rounded-xl border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] p-3">
              <PdfDraftStack />
            </div>
          </div>
        </div>
      </div>
    </MockWindow>
  );
};

export const MockupArrow = ArrowUpRight;
export const _palette = { BRASS, BRASS2, SAND, TEXT, TEXT2, MUTED, HAIR, GREEN };
